import React, { useState } from 'react';
import { ArrowUpRight, GraduationCap } from 'lucide-react';

import { useJourneyMapsForArchitecture } from '../../../hooks/related-by-tag-hooks';
import { useNearViewport } from '../../Common/RelatedByTag';

/** Journeys listed before "Show all". */
const COLLAPSED_COUNT = 5;

/**
 * Cyber Academy journeys tagged like this architecture. A text block on the left and a list of rows on the
 * right, so a long list stays compact (the first few show, the rest sit behind a button) and the section
 * reads differently from the card sections around it. The lists behind it take a few requests and the page
 * is built to load with one, so they load once the reader scrolls near it.
 */
const ArchitectureLearningJourneys: React.FC<{ gtmId: number | null | undefined }> = ({ gtmId }) => {
  const { ref, near } = useNearViewport();
  const { data } = useJourneyMapsForArchitecture(near ? gtmId : null);
  const [expanded, setExpanded] = useState(false);

  if (!data.length) return <div ref={ref} aria-hidden="true" />;

  const shown = expanded ? data : data.slice(0, COLLAPSED_COUNT);
  const hidden = data.length - shown.length;

  return (
    <section id="learning-journeys" className="py-20 scroll-mt-16 bg-[#0d2036] border-y border-white/5">
      <div className="max-w-[1120px] mx-auto px-5 lg:px-10 grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-10 lg:gap-16">
        <div>
          <div className="inline-flex items-center gap-2 text-[#34d399] text-xs font-bold uppercase tracking-widest mb-4">
            <GraduationCap className="w-4 h-4" />
            Cyber Academy
          </div>
          <h2 className="text-3xl font-bold text-white mb-3">Learning journeys</h2>
          <p className="text-[#8eafc8] leading-relaxed">
            Guided training that covers this architecture. Each one opens in Cyber Academy.
          </p>
          <p className="mt-4 text-[#6b8299] text-sm">
            {data.length} {data.length === 1 ? 'journey' : 'journeys'}
          </p>
        </div>

        <div>
          <ul className="divide-y divide-white/10 border-y border-white/10">
            {shown.map((m) => (
              <li key={m.id}>
                <a
                  href={m.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-4 py-4 px-1 transition-colors hover:bg-white/[0.03]"
                >
                  <span className="w-10 h-10 shrink-0 rounded-lg bg-[#0f2035] border border-white/10 flex items-center justify-center text-[#34d399]">
                    <GraduationCap className="w-5 h-5" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-white font-semibold truncate group-hover:text-[#16bdeb]">{m.name}</span>
                    <span className="block text-[#6b8299] text-sm truncate">
                      {[m.typeName, m.description].filter(Boolean).join(' · ') || 'Cyber Academy training'}
                    </span>
                  </span>
                  <ArrowUpRight className="w-5 h-5 shrink-0 text-[#3a5068] group-hover:text-[#049fd9]" aria-hidden="true" />
                </a>
              </li>
            ))}
          </ul>
          {data.length > COLLAPSED_COUNT && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              className="mt-4 text-[#049fd9] text-sm font-semibold hover:text-[#16bdeb]"
            >
              {expanded ? 'Show fewer' : `Show ${hidden} more`}
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

export default ArchitectureLearningJourneys;
